import type { ReferralLink } from '@/types/referral';

/**
 * Merge-tag registry. Hardcoded client-side because the substitutions
 * are static and tied to fields the client already has — no backend
 * round-trip needed for the registry itself.
 *
 * Insert sheet groups: "Recipient" tags read from the recipient link;
 * "Your stuff" tags read from the same recipient context. (The old
 * "sender's generic link" fallback for `{{your_link}}` is gone — every
 * link is now deal-anchored, and the caller is responsible for passing
 * the correct deal's tracked URL via `recipient.tracked_url`.)
 */
export const MERGE_TAG_REGISTRY = [
  { token: 'first_name', label: 'First name', group: 'recipient', sample: 'Sarah' },
  { token: 'last_name', label: 'Last name', group: 'recipient', sample: 'L.' },
  { token: 'your_link', label: 'Your link', group: 'your_stuff', sample: 'marketpryce.com/?r=ABC123' },
] as const satisfies readonly MergeTagSpec[];

export type MergeTag = (typeof MERGE_TAG_REGISTRY)[number]['token'];

export type MergeTagSpec = {
  token: string;
  label: string;
  group: 'recipient' | 'your_stuff';
  /** Sample value shown in the Insert sheet for context. */
  sample: string;
};

export type RecipientForRender = {
  /** Display name captured at link creation (link.label). */
  name: string;
  /** Optional first/last derived from the captured name. */
  first_name?: string | null;
  last_name?: string | null;
  /**
   * The recipient's own tracked share URL (`?r=<token>`), if they were
   * built from a person/campaign ReferralLink. When set, `{{your_link}}`
   * resolves to this URL so each recipient gets a per-person tracked
   * link instead of the sender's generic link — preserves the whole
   * point of tracked links during bulk send.
   */
  tracked_url?: string | null;
};

const TOKEN_RE = /\{\{(\w+)\}\}/g;

/**
 * Substitute `{{token}}` placeholders in `body`. Unknown tokens and
 * tokens that can't be resolved yet (`{{your_link}}` before the store
 * loads) are left as literal text — this is end-user output, not
 * server validation, and a visible `{{your_link}}` is the signal to
 * the sender that the URL isn't ready yet.
 */
export function renderTemplate(
  body: string,
  recipient: RecipientForRender | null,
): string {
  return body.replace(TOKEN_RE, (match, raw) => {
    const value = lookupToken(raw, recipient);
    if (value === null) return match; // unknown or unresolved — leave literal
    return value;
  });
}

/**
 * Look up a single token's substituted value. Returns null for unknown
 * tokens AND for `your_link` when no URL source is available, so the
 * caller can decide how to render them (literal text in `renderTemplate`,
 * dimmed pill in Compose preview).
 */
export function lookupToken(
  token: string,
  recipient: RecipientForRender | null,
): string | null {
  switch (token) {
    case 'first_name':
      return recipient?.first_name?.trim() || splitName(recipient?.name).first;
    case 'last_name':
      return recipient?.last_name?.trim() || splitName(recipient?.name).last;
    case 'your_link':
      // No silent fallback: callers must supply `recipient.tracked_url`
      // sourced from a deal-anchored ReferralLink. When unset,
      // renderTemplate leaves `{{your_link}}` as a visible literal so
      // the sender notices the URL isn't ready.
      return recipient?.tracked_url ?? null;
    default:
      return null;
  }
}

/**
 * Build a `RecipientForRender` from a ReferralLink. Used by Compose's
 * preview block and the Sending screen's per-recipient render.
 *
 * Name comes from the link's saved label (ambassador-typed at link
 * creation). If the label has multiple words, treat the first as
 * first_name and the rest as last_name.
 */
export function recipientFromLink(
  link: Pick<ReferralLink, 'label' | 'public_url'>,
): RecipientForRender {
  const { first, last } = splitName(link.label);
  return {
    name: link.label,
    first_name: first,
    last_name: last,
    tracked_url: link.public_url ?? null,
  };
}

function splitName(name: string | null | undefined): { first: string; last: string } {
  if (!name) return { first: '', last: '' };
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  return {
    first: parts[0] ?? '',
    last: parts.slice(1).join(' '),
  };
}

